import React from 'react';
import { SURFACE_CENTRAL, TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../../../utils/statusColors';
import { campo, campoComEstado } from '../../../design';
import { fmtDateTime } from '../../../utils/formatters';
import {
  chaveDaLinha, chaveDeExibicao, envioRecusadoPeloPortal, faltaEfetiva, FRASE_CONFIRMACAO_FINAL, FRASE_DO_PULADO, FRASE_DUPLICADO,
  FRASE_ESTADO_MISTO, FRASE_NAO_CADASTRADO, FRASE_SEM_VINCULO, fraseDaRecusa, INSTRUCAO_DEPOIS_DE_GRAVAR, jobTerminou,
  linhaEditavel, oQueFalta, placarDaGravacao, propostaEnviadaPeloHub, resumoDoEnvio, valorDivergente, valorNaTela,
} from '../../../utils/gravacaoNoPortal';
import type { LinhaDaPrevia, PreviaDaGravacao } from '../../../utils/gravacaoNoPortal';
import type { ConfirmOptions } from '../../shared/ConfirmDialog';
import type { EstadoDoSalvamento, GravacaoNoPortal } from '../../../hooks/useGravacaoNoPortal';

/**
 * A etapa Envio do portal que GRAVA ITENS e para (`cadastrar.modo === 'grava_itens'`; Licitanet, 18/10/2026; ver
 * utils/gravacaoNoPortal). Quatro tempos:
 * 1. a PRÉVIA do que vai em cada item, com marca e modelo editáveis (nascem com o cadastro; o que faltar fica em destaque);
 * 2. o botão "Gravar itens no portal" (não "Enviar proposta": o envio final é do Heitor, no portal);
 * 3. o resultado item a item do que o PORTAL devolveu;
 * 4. a instrução de conferir, anexar e confirmar no portal, com o "Já enviei" em destaque.
 *
 * ⭐ Proposta que ainda NÃO existe no portal (`previa.exigeConfirmacao`, decisão do Heitor de 19/10 21:05): o botão vira
 * "Enviar proposta ao portal", com confirmação explícita (`opcoesDaConfirmacaoDoEnvio`), e o HUB cadastra e envia. Com o
 * sim do PORTAL: o placar e a proposta marcada como enviada (pelo backend). Com o não: a frase DO PORTAL, uma vez.
 */

/** A confirmação do envio: itens, total, ME/EPP e a frase de que é a confirmação final. Usada pelos dois botões. */
export function opcoesDaConfirmacaoDoEnvio(previa: PreviaDaGravacao): ConfirmOptions {
  const r = resumoDoEnvio(previa);
  return {
    title: 'Enviar a proposta ao portal?',
    message: (
      <div data-confirmacao-do-envio className="space-y-2">
        <p>A proposta ainda não existe no portal: o HUB vai cadastrá-la e enviá-la na Licitanet (pregão {previa.codPregao}).</p>
        <ul className="list-disc pl-5">
          <li data-envio-itens>{r.itens} {r.itens === 1 ? 'item' : 'itens'}</li>
          <li data-envio-total>Total: {r.total}</li>
          <li data-envio-meepp>ME/EPP: {r.meEpp}</li>
        </ul>
      </div>
    ),
    consequence: FRASE_CONFIRMACAO_FINAL,
    confirmLabel: 'Enviar ao portal',
    danger: true,
  };
}

interface Props {
  g: GravacaoNoPortal;
  onJaEnviei: () => void;
  /** A sessão do pregão no portal (`item.linkOrigem`), para o "Ver no portal" depois de um envio que não deu certo. */
  linkDoPortal?: string | null;
}

/** Não há link da sessão: a entrada do portal. */
const PORTAL_PADRAO = 'https://licitanet.com.br';

const botaoPrincipal = 'px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors';
const botaoNeutro = 'px-3 py-2 bg-central-cartao border border-central-borda hover:bg-central-elevado disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 rounded-lg text-xs transition-colors';

/** Ao lado do campo: salvando, salvo no cadastro, ou o erro do salvar (que não some sozinho). */
function SeloDoSalvamento({ s }: { s?: EstadoDoSalvamento }) {
  if (!s) return null;
  if (s.estado === 'salvando') return <div data-salvamento="salvando" className="mt-0.5 text-[10px] text-slate-500">salvando…</div>;
  if (s.estado === 'salvo') return <div data-salvamento="salvo" className={`mt-0.5 text-[10px] ${TONE_TEXT.success}`}>salvo no cadastro</div>;
  return <div data-salvamento="erro" role="alert" className={`mt-0.5 text-[10px] ${TONE_TEXT.danger}`}>não salvou: {s.erro}. Saia do campo de novo para tentar.</div>;
}

export default function GravarItensNoPortal({ g, onJaEnviei, linkDoPortal }: Props) {
  const { previa, carregando, erroDaPrevia, preenchido, job, gravando, recusa, erro, podeGravar } = g;

  if (carregando && !previa) return <div className={`${SURFACE_CENTRAL.card} p-4 text-sm text-slate-400`}>Montando a prévia do que vai ao portal…</div>;
  if (erroDaPrevia && !previa) {
    return (
      <div role="alert" className={`${SURFACE_CENTRAL.card} p-4 text-sm space-y-2`}>
        <div className={TONE_TEXT.danger}>Não consegui montar a prévia: {erroDaPrevia}. Nada foi gravado no portal.</div>
        <button type="button" onClick={() => g.recarregar()} className={botaoNeutro}>Tentar de novo</button>
      </div>
    );
  }
  if (!previa) return null;

  const total = previa.linhas.length;
  // Terminou COM resultado (done, ou failed no meio: a `ultimaGravacao` guarda o que foi gravado até ali).
  const comResultado = jobTerminou(job) && !!job?.resultado && !gravando;
  const placar = comResultado ? placarDaGravacao(job, total) : null;
  // O resultado casa com a linha por lote + item: o codItem pode vir 0 ou repetido (Ituiutaba, 18/10).
  const porItem = new Map((job?.resultado?.itens ?? []).map((i) => [chaveDeExibicao(i), i]));
  const devolvidoDe = (l: LinhaDaPrevia) => porItem.get(chaveDeExibicao(l));
  const recusaVisivel = recusa || previa.recusa;
  // MARCA_MODELO_FALTANDO o corpo supre: é aviso com formulário, não bloqueio; some quando tudo foi completado.
  const suprivel = !!recusaVisivel?.suprivelPeloCorpo;
  const temRecusado = !!placar && (placar.recusados > 0 || placar.semResposta > 0);
  const temLote = previa.linhas.some((l) => l.lote != null && l.lote !== 1);
  // Os itens com marca/modelo vazios AGORA, pelo que está na tela: a lista da recusa da prévia envelhece quando o
  // Heitor completa um item ou esvazia outro. "Complete nos campos em destaque" só quando há campo (linha com chave).
  const semMarcaAgora = previa.linhas.filter((l) => faltaEfetiva(l, preenchido, previa.linhas).some((f) => f === 'marca' || f === 'modelo'));
  const temCampo = semMarcaAgora.some((l) => linhaEditavel(l, previa.linhas));
  const resumoDaFalta = oQueFalta(previa, preenchido);
  // Proposta que ainda não existe no portal: gravar é CADASTRAR e ENVIAR.
  const enviar = previa.exigeConfirmacao === true;
  const doEnvio = comResultado && job?.resultado?.caminho === 'criar';
  const enviadaOk = doEnvio && propostaEnviadaPeloHub(job);
  const envioRecusado = doEnvio && envioRecusadoPeloPortal(job);
  const veredito = job?.resultado?.envio ?? null;
  // ⛔ Depois de um envio que o portal recusou, ou cujo destino não se sabe (falha sem código depois do pedido), o HUB
  // NÃO oferece mandar de novo: repetir um POST que talvez tenha chegado deixa DUAS propostas no órgão (Bastião, 19/10).
  // O caminho é ver no portal; se a proposta estiver lá, a próxima sincronização a traz e a tela passa a corrigir.
  const envioIncerto = enviar && !!erro && !gravando;
  const semNovoEnvio = envioRecusado || envioIncerto;
  const rotuloDoBotao = gravando ? (enviar ? 'Enviando ao portal…' : 'Gravando no portal…') : enviar ? 'Enviar proposta ao portal' : 'Gravar itens no portal';

  return (
    <div data-gravar-itens className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm text-slate-200 font-medium">Gravar itens no portal</div>
        <span className="text-[11px] text-slate-500 font-mono">pregão {previa.codPregao}</span>
        {previa.optarMeEpp && <span className={`text-[11px] px-1.5 py-0.5 rounded ${TONE_BADGE.neutral}`}>ME/EPP: sim</span>}
      </div>
      <p className="text-xs text-slate-400">
        {enviar
          ? 'A proposta ainda não existe no portal: o HUB vai cadastrá-la e enviá-la com estes itens (valor, marca e modelo). Confira cada linha: o envio pede a sua confirmação e não tem desfazer. Os anexos, se houver, são seus, na tela do portal.'
          : 'O HUB grava valor, marca e modelo de cada item e para. A confirmação final do envio e os anexos são seus, na tela do portal.'}
      </p>
      {previa.estadoMisto && (
        <div role="status" data-estado-misto className={`rounded-lg border p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>{FRASE_ESTADO_MISTO}</div>
      )}

      {gravando && (
        <div data-gravando className={`text-xs ${TONE_TEXT.info}`}>
          {enviar ? 'Enviando ao portal' : 'Gravando no portal'}{job?.progresso && !enviar ? `: ${job.progresso.feitos} de ${job.progresso.total} itens` : '…'}
        </div>
      )}

      {/* 1 e 3: a prévia, e ao lado de cada item o que o PORTAL devolveu */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="py-1 pr-2">{temLote ? 'Lote/item' : 'Item'}</th><th className="py-1 pr-2">Descrição</th>
              <th className="py-1 pr-2 text-right">Qtd</th><th className="py-1 pr-2 text-right">Valor unit.</th>
              <th className="py-1 pr-2">Marca</th><th className="py-1 pr-2">Modelo</th>
              {job?.resultado && <th className="py-1">No portal</th>}
            </tr>
          </thead>
          <tbody>
            {previa.linhas.map((l) => {
              const k = chaveDaLinha(l, previa.linhas);
              const editavel = linhaEditavel(l, previa.linhas);
              const falta = faltaEfetiva(l, preenchido, previa.linhas);
              const outra = falta.filter((f) => f !== 'marca' && f !== 'modelo');
              const devolvido = devolvidoDe(l);
              return (
                <tr key={chaveDeExibicao(l)} data-linha={l.numItem} className="border-t border-central-borda align-top">
                  <td className="py-1.5 pr-2 font-mono text-slate-300">{temLote ? `${l.lote ?? '—'}/${l.numItem}` : l.numItem}</td>
                  <td className="py-1.5 pr-2 text-slate-200">
                    {l.descricao}
                    {outra.length > 0 && (
                      <div data-falta-na-linha className={`mt-0.5 ${TONE_TEXT.danger}`}>
                        {outra.includes('codItem') && <div data-sem-vinculo>{FRASE_SEM_VINCULO}</div>}
                        {outra.includes('duplicado') && <div data-duplicado>{FRASE_DUPLICADO}</div>}
                        {outra.includes('preco') && <div>Sem preço.</div>}
                        {outra.includes('codProposta') && <div data-nao-cadastrado>{FRASE_NAO_CADASTRADO}</div>}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 text-right font-mono text-slate-300">{l.quantidade}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-slate-200">{l.valorUnitario ? `R$ ${l.valorUnitario}` : '—'}</td>
                  {(['marca', 'modelo'] as const).map((c) => (
                    <td key={c} className="py-1.5 pr-2">
                      {k != null && editavel && !gravando ? (
                        <>
                          <input
                            aria-label={`${c === 'marca' ? 'Marca' : 'Modelo'} do item ${l.numItem}`}
                            value={valorNaTela(l, preenchido, c, previa.linhas)}
                            onChange={(e) => g.completar(k, c, e.target.value)}
                            onBlur={() => g.salvar(k, c)}
                            placeholder="falta no cadastro"
                            className={`w-full min-w-[7rem] rounded px-2 py-1 text-xs outline-none ${falta.includes(c) ? campoComEstado('border-amber-500/70') : campo}`}
                          />
                          <SeloDoSalvamento s={g.salvamento[k]?.[c]} />
                        </>
                      ) : (
                        <span className="text-slate-200">{(editavel ? valorNaTela(l, preenchido, c, previa.linhas) : l[c]) || '—'}</span>
                      )}
                    </td>
                  ))}
                  {job?.resultado && (
                    <td className="py-1.5" data-devolvido={devolvido?.status ?? 'sem-resposta'}>
                      {devolvido?.status === 'ok' && (
                        <span className={`px-1.5 py-0.5 rounded ${TONE_BADGE.success}`}>
                          {job?.resultado?.caminho === 'criar' ? 'enviado' : 'gravado'}{devolvido.valorGravado ? ` · R$ ${devolvido.valorGravado}` : ''}
                        </span>
                      )}
                      {devolvido?.status === 'ok' && valorDivergente(l.valorUnitario, devolvido.valorGravado) && (
                        <div data-valor-divergente className={`mt-0.5 ${TONE_TEXT.warning}`}>o portal gravou um valor diferente da prévia</div>
                      )}
                      {devolvido?.status === 'pulado' && (
                        <span data-pulado title={FRASE_DO_PULADO}>
                          <span className={`px-1.5 py-0.5 rounded ${TONE_BADGE.neutral}`}>mantido</span>
                          <span className="block mt-0.5 text-slate-500">{FRASE_DO_PULADO}</span>
                        </span>
                      )}
                      {devolvido && devolvido.status !== 'ok' && devolvido.status !== 'pulado' && (
                        // O veredito do envio é um só, replicado em cada item: a frase vai uma vez, embaixo, não 13.
                        <span className={TONE_TEXT.danger}>recusado pelo portal{devolvido.mensagem && !veredito?.vereditoReplicado ? `: ${devolvido.mensagem}` : ''}</span>
                      )}
                      {!devolvido && <span className="text-slate-500">sem resposta do portal</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* recusa antes de gravar (ou do job): diz qual, com os itens */}
      {recusaVisivel && !gravando && suprivel && !podeGravar && semMarcaAgora.length > 0 && (
        <div role="status" data-recusa={recusaVisivel.codigo} className={`rounded-lg border p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
          {fraseDaRecusa({ ...recusaVisivel, itens: semMarcaAgora.map((l) => l.numItem) })}{temCampo ? ' Complete nos campos em destaque e grave.' : ''}
        </div>
      )}
      {recusaVisivel && !gravando && !suprivel && (
        <div role="alert" data-recusa={recusaVisivel.codigo} className={`rounded-lg border p-3 text-xs ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>
          Nada foi gravado: {fraseDaRecusa(recusaVisivel)}
        </div>
      )}
      {erro && !gravando && (
        <div role="alert" data-erro-da-gravacao className={`rounded-lg border p-3 text-xs ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
          {erro}
          {envioIncerto && <div data-envio-incerto className="mt-1">O envio pode ter chegado ao portal: confira na tela da Licitanet antes de qualquer coisa. O HUB não envia de novo.</div>}
        </div>
      )}

      {/* 3 e 4: o placar e o que fica com o Heitor */}
      {enviadaOk && (
        <div data-envio-ok className="space-y-2">
          <div data-placar className={`text-sm ${TONE_TEXT.success}`}>
            Proposta enviada ao portal: {placar?.gravados ?? 0} de {total} itens{job?.concluidoEm ? ` · ${fmtDateTime(job.concluidoEm)}` : ''}
          </div>
          {veredito?.mensagem && <div data-resposta-do-portal className="text-xs text-slate-300">Resposta do portal: {veredito.mensagem}</div>}
          <p className="text-sm text-slate-200">A proposta foi marcada como enviada. Os anexos, se houver, sobem na tela da Licitanet.</p>
        </div>
      )}
      {envioRecusado && (
        <div role="alert" data-envio-recusado className={`rounded-lg border p-3 text-xs ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>
          O portal recusou o envio{veredito?.status ? ` (HTTP ${veredito.status})` : ''}: {(veredito?.mensagem || job?.resumo || 'sem mensagem').replace(/[.\s]+$/, '')}. A proposta não foi
          marcada como enviada. Não envie de novo pelo HUB: confira na tela da Licitanet. Se a proposta estiver lá, sincronize
          do portal; se não estiver, cadastre-a lá e clique em Já enviei.
        </div>
      )}
      {job?.resultado?.confirmacaoIgnorada && comResultado && (
        <div role="status" data-confirmacao-ignorada className={`text-xs ${TONE_TEXT.warning}`}>
          {job.resultado.motivoDaIgnoracao || 'A proposta já existia no portal: o HUB corrigiu os itens em vez de cadastrar outra.'} Nada foi enviado de novo.
        </div>
      )}
      {placar && !doEnvio && (
        <div data-depois-de-gravar className="space-y-2">
          <div data-placar className={`text-xs ${placar.todos ? TONE_TEXT.success : TONE_TEXT.warning}`}>
            {placar.gravados + placar.pulados} de {total} itens no portal
            {placar.pulados ? ` (${placar.pulados} mantidos: já gravados iguais)` : ''}
            {placar.recusados ? ` · ${placar.recusados} recusado(s)` : ''}
            {placar.semResposta ? ` · ${placar.semResposta} sem resposta` : ''}
            {job?.concluidoEm ? ` · ${fmtDateTime(job.concluidoEm)}` : ''}
          </div>
          {job?.resumo && <div data-resumo className="text-[11px] text-slate-400">{job.resumo}</div>}
          {job?.status === 'failed' && (
            <div className={`text-[11px] ${TONE_TEXT.warning}`}>A gravação parou no meio{job.erro ? ` (${job.erro})` : ''}: o que aparece acima é o que chegou ao portal.</div>
          )}
          {/* Com item recusado ou sem resposta, "confirme o envio lá" leria como pronto: primeiro o item. */}
          {temRecusado && (
            <p data-falta-gravar className={`text-sm ${TONE_TEXT.warning}`}>
              Há item que não chegou ao portal: corrija e grave de novo, ou ajuste direto no portal. Depois:
            </p>
          )}
          <p className="text-sm text-slate-200">{INSTRUCAO_DEPOIS_DE_GRAVAR}</p>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {enviadaOk ? null : semNovoEnvio ? (
          <>
            <a href={linkDoPortal || PORTAL_PADRAO} target="_blank" rel="noopener noreferrer" data-ver-no-portal className={botaoPrincipal}>Ver no portal</a>
            <button type="button" data-ja-enviei onClick={onJaEnviei} className={botaoNeutro}>Já enviei</button>
          </>
        ) : placar && !doEnvio ? (
          <>
            <button type="button" data-ja-enviei onClick={onJaEnviei} className={temRecusado ? botaoNeutro : botaoPrincipal}>Já enviei</button>
            {temRecusado && (
              <button type="button" data-gravar onClick={() => g.gravar()} disabled={!podeGravar} className={botaoPrincipal}>
                Gravar de novo
              </button>
            )}
          </>
        ) : (
          <button type="button" data-gravar data-enviar={enviar || undefined} onClick={() => g.gravar()} disabled={!podeGravar} className={botaoPrincipal}
            title={podeGravar ? undefined : gravando ? rotuloDoBotao : `Complete o que falta antes de ${enviar ? 'enviar' : 'gravar'}`}>
            {rotuloDoBotao}
          </button>
        )}
        {!gravando && !enviadaOk && (!recusaVisivel || suprivel) && resumoDaFalta && (
          <span data-o-que-falta className="text-xs text-slate-400">Antes de {enviar ? 'enviar' : 'gravar'}: {resumoDaFalta}.</span>
        )}
      </div>
    </div>
  );
}
